import { Conference } from "@easycv/common/types/conference";
import { Hierarchy, HierarchyNames } from "@easycv/common/types/util";
import { breakPath } from "../../lib/hierarchyUtilities.js";
import { CCStyling } from "./CCStyling";
import { CCInputVariant } from "./types/variants";

/**
 *  trySortByKeyEnding
 *  CCInteractiveTables store entries in the database with the key:
 *    '[key defined by structure]-[index of entry in table]'.
 *  So if the table has the key `custom-interactive-table`,
 *  there would be an object in the database with the key `custom-interactive-table`.
 *  That object would store the entries of the table, with the first entry as 'custom-interactive-table-0',
 *  and the second entry as 'custom-interactive-table-1', etc..
 *
 *  This is fine until we want to store a specific sorting of the entires of the table.
 *  Say we create a table with the elements initially sorted, so something like:
 *  ```
 *  custom-interactive-table: {
 *    ...
 *    custom-interactive-table-3: 10,
 *    custom-interactive-table-4: 11,
 *    ...
 *    custom-interactive-table-33: 29
 *  }
 *  ```
 *  When this is reloaded, the `custom-interactive-table-33` will come before `custom-interactive-table-4`
 *  because, in terms of sorting, the string value '33' is lower than the string value '4'
 *    ("33" < "4" == true   vs    33 < 4 == false)
 *
 *  So all this function does is take the number at the end of the key (33 or 4 in this example),
 *  converts it to an actual number, and then returns the ordering of those numbers.
 */
export function trySortByKeyEnding(x: string, y: string): number {
  // Get the last part of the key, (xk = 'ab-cd-1' -> '1')
  let xk = x?.split("-")?.pop();
  // If it's null, the number version is NaN, or the number version is infinite,
  // reassign to the whole string. (xk = 'ab-cd-1' -> 'ab-cd-1')
  if (!Boolean(xk) || isNaN(+xk) || !isFinite(+xk)) xk = x;

  // Get the last part of the other key, (yk = 'ab-cd-1' -> '1')
  let yk = y?.split("-")?.pop();
  // If it's null, the number version is NaN, or the number version is infinite,
  // reassign to the whole string. (yk = 'ab-cd-1' -> 'ab-cd-1')
  if (!Boolean(yk) || isNaN(+yk) || !isFinite(+yk)) yk = y;

  // Here we are either doing the same check or checking the full original 'x'/'y'
  if (
    !Boolean(xk) ||
    !Boolean(yk) ||
    isNaN(+xk) ||
    isNaN(+yk) ||
    !isFinite(+xk) ||
    !isFinite(+yk)
  ) {
    // If theres an issue, say the elements are equal
    return 0;
  }
  // Return the ordering of the numbers.
  return +xk - +yk;
}

export function convertInputVariantToType(variant: CCInputVariant): string {
  return {
    String: "text",
    Date: "date",
    Float: "text",
    Integer: "text",
    Check: "checkbox",
    Paragraph: "",
    Select: ""
  }[variant];
}

export function convertAttrsToString(
  htmlAttributes: Record<string, string>
): string {
  return Object.entries(htmlAttributes)
    .reduce((acc, [key, val]) => {
      return [...acc, `${key}="${val}"`];
    }, [])
    .join(" ");
}

export function createElement(
  tag: keyof HTMLElementTagNameMap,
  classList: string,
  style: CCStyling,
  content: string,
  htmlAttributes: Record<string, string> = {}
): string {
  return `<${tag} class="${classList}" ${convertAttrsToString(htmlAttributes)} style="${style.compile()}">${content}</${tag}>`;
}

export function getDivisionChoices(
  conference: Conference,
  hierarchy: Hierarchy,
  hierarchyNames: HierarchyNames
) {
  let choices = [];
  const brokenPaths = conference.paths.map((path) =>
    breakPath(path, hierarchy, hierarchyNames)
  );
  if (brokenPaths.length) {
    const { hospitalId, deptId } = brokenPaths[0];
    // Assume there is one department and if there is more than one department, do nothing.
    if (
      brokenPaths.every(
        ({ hospitalId: hid, deptId: did }) => hid == hospitalId && did == deptId
      )
    ) {
      choices = Object.keys(hierarchy?.[hospitalId]?.depts[deptId] ?? []).map(
        (divId) => {
          return hierarchyNames[divId];
        }
      );
    }
  }
  choices.sort();
  return choices;
}

/* Private HTML to XML functions and variables, not intended for use outside of this file */

interface _ParserNode {
  tag: string;
  children: (_ParserNode | string)[];
}
const _allowedRichTextTags = [
  "i",
  "em",
  "b",
  "strong",
  "u",
  "sup",
  "sub",
  "a",
  "p",
  "br"
];
const _linkRegex =
  /<a href=(["'])(.*?)\1( target=(["']).*?\3 rel=(["']).*?\4)?>/;
function _substringIsStillValid(substring: string): boolean {
  const filtered = substring.replaceAll(/[\/<>]/g, "");
  for (const tag of _allowedRichTextTags) {
    if (tag.includes(filtered)) {
      return true;
    }
  }
  return false;
}
function _isRichTextTag(tag: string): boolean {
  if (tag == undefined || tag.length < 2) return false;
  if (tag.charAt(0) === "<" && tag.charAt(tag.length - 1) === ">") {
    if (tag.charAt(1) === "/") {
      return _allowedRichTextTags.includes(tag.substring(2, tag.length - 1));
    } else {
      return _allowedRichTextTags.includes(tag.substring(1, tag.length - 1));
    }
  }
  return false;
}
interface _WRTagFlags {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  hyperlink: boolean;
  superscript: boolean;
  subscript: boolean;
}

function _buildWRTag(
  flags: _WRTagFlags,
  font: string,
  text: string,
  links: { id: number; link: string }[],
  linkIdBase: number
): [string, number] {
  let justBrTest = /<\s*br\s*\/\s*>/g.exec(text);
  if (justBrTest && justBrTest[0] === justBrTest.input) {
    return ["", linkIdBase];
  }
  text = text.replaceAll(/(^<\s*br\s*\/\s*>)|(<\s*br\s*\/\s*>$)/g, "");
  text = text.replaceAll(/<\s*br\s*\/\s*>/g, "<w:br/>");

  const hyperlinkTag = () => {
    return `<w:hyperlink r:id="rId${links[linkIdBase++].id}">`;
  };
  let script = flags.superscript || flags.subscript;
  let str = `${script ? "" : "<w:p>"}${flags.hyperlink ? hyperlinkTag() : ""}<w:r><w:rPr>${font}`;
  if (flags.bold) {
    str += "<w:b/><w:bCs/>";
  }
  if (flags.underline) {
    str += '<w:u w:val="single"/><w:uCs w:val="single"/>';
  }
  if (flags.italic) {
    str += "<w:i/><w:iCs/>";
  }
  if (flags.superscript) {
    str += '<w:vertAlign w:val="superscript"/>';
  }
  if (flags.subscript) {
    str += '<w:vertAlign w:val="subscript"/>';
  }
  if (flags.hyperlink) {
    str += '<w:rStyle w:val="Hyperlink"/>';
  }
  str += "</w:rPr>";
  str += `<w:t xml:space="preserve">${text}</w:t>`;
  str += `</w:r>${flags.hyperlink ? "</w:hyperlink>" : ""}${script ? "" : "</w:p>"}`;
  return [str, linkIdBase];
}
function _setWRFlags(flags: _WRTagFlags, node: _ParserNode) {
  if (node.tag === "<i>" || node.tag === "<em>") {
    flags.italic = !Boolean(flags.italic);
  } else if (node.tag === "<b>" || node.tag === "<strong>") {
    flags.bold = !Boolean(flags.bold);
  } else if (node.tag === "<u>") {
    flags.underline = !Boolean(flags.underline);
  } else if (node.tag === "<a>") {
    flags.hyperlink = !Boolean(flags.hyperlink);
  } else if (node.tag === "<sup>") {
    flags.superscript = !Boolean(flags.superscript);
  } else if (node.tag === "<sub>") {
    flags.subscript = !Boolean(flags.subscript);
  }
}
function _walkHTMLTree(
  font: string,
  node: _ParserNode | string,
  links: { id: number; link: string }[],
  linkId: number,
  result: string[],
  flags: _WRTagFlags = {
    italic: false,
    hyperlink: false,
    underline: false,
    bold: false,
    subscript: false,
    superscript: false
  }
) {
  if (typeof node === "string") {
    const build = _buildWRTag(flags, font, node, links, linkId);
    if (flags.superscript || flags.subscript) {
      // Sub/superscript needs to be inserted into last paragraph so we gotta do this splicing stuff
      let lastResult = result[result.length - 1];
      let lastIndexOfTag = lastResult.lastIndexOf("<");
      if (lastIndexOfTag != -1) {
        result[result.length - 1] =
          lastResult.substring(0, lastIndexOfTag) +
          build[0] +
          lastResult.substring(lastIndexOfTag);
      }
    } else {
      result.push(build[0]);
    }
    linkId = build[1];
  } else {
    _setWRFlags(flags, node);
    for (const child of node.children) {
      result = _walkHTMLTree(font, child, links, linkId, result, { ...flags });
    }
  }
  return result;
}
function _fontToString({ font, size }: DXT.OutputFontOptions): string {
  return `<w:rFonts w:ascii="${font}" w:hAnsi="${font}"/><w:sz w:val="${parseInt(size) * 2}"/>`;
}

export function arrayWithFlagsFromJSObject({
  links,
  obj
}: {
  links: any[];
  obj: _ParserNode[];
}): { flags: _WRTagFlags; text: string; hyperlink: undefined | string }[] {
  const result = [];
  let linkIndex = 0;
  const walker = (
    node: _ParserNode | string,
    flags: _WRTagFlags = {
      italic: false,
      hyperlink: false,
      underline: false,
      bold: false,
      subscript: false,
      superscript: false
    }
  ) => {
    if (typeof node === "string") {
      result.push({
        flags: { ...flags },
        text: node,
        hyperlink:
          flags.hyperlink && links?.[linkIndex]
            ? links[linkIndex++].link
            : undefined
      });
    } else {
      _setWRFlags(flags, node);
      for (const child of node.children) {
        walker(child, { ...flags });
      }
    }
  };
  for (const root of obj) walker(root);
  return result;
}

export function parseHTMLIntoJSObject(
  data: string,
  firstLinkIdWeCanAdd: number
): { links: any[]; obj: _ParserNode[] } {
  if (typeof data !== "string") {
    // Not a string, give up
    // Pretty much only happens when data is undefined
    return { links: [], obj: data };
  }
  let tree: _ParserNode[] = [
    {
      tag: "<p>",
      children: []
    }
  ];
  // <a> tags have a lot of attributes (href, target, rel).
  // This takes those tags, extracts the link and inner text, and replaces it with <a>inner</a>
  const links = [];
  let linkMatch: RegExpExecArray;
  while ((linkMatch = _linkRegex.exec(data)) != undefined) {
    data =
      data.substring(0, linkMatch.index) +
      "<a>" +
      data.substring(linkMatch.index + linkMatch[0].length);
    links.push({
      id: firstLinkIdWeCanAdd++,
      link: linkMatch[2]
    });
  }

  if (!data.includes("<") || !data.includes(">")) {
    // Data is either malformed or not HTML. Wrap in minimum XML and give up.
    tree[0].children.push(data);
    return {
      links,
      obj: tree
    };
  }

  let current = tree[0];
  let prev = tree[0];
  let chunks = [""];
  // For each char in data
  for (let i = 0; i < data.length; ++i) {
    // If its the start of a tag
    if (data.charAt(i) === "<") {
      // Look at the next character
      let close = i + 1;
      // While the next character isn't the close of the tag
      while (data.charAt(close) !== ">") {
        // If the tag is still valid,
        if (_substringIsStillValid(data.substring(i, close))) {
          // Move to the next character
          close++;
          if (close >= data.length) {
            // Infinite loop protection, I don't think code can ever get here realistically
            close = NaN;
            break;
          }
        } else {
          // Else the tag is invalid, the '<' character was just something in the text iteself.
          break;
        }
      }
      if (isNaN(close)) break;
      // substring(i, close + 1) is our full tag. So '<strong>', '<em>', etc
      const tag = data.substring(i, close + 1);
      // If we broke above in the else of '_substringIsStillValid', this if is false
      if (_isRichTextTag(tag)) {
        // If it's a closing tag, it will have a '/'
        if (tag.includes("/")) {
          // Push content onto children
          current.children.push(...chunks);
          // Filter blank children
          current.children = current.children.filter((child) => child != "");
          // Set current to tree[0]
          current = prev;
        } else {
          // Else, it's an opening tag
          // Push content onto children
          current.children.push(...chunks);
          // Push this next tag onto children
          current.children.push({
            tag: tag,
            children: []
          });
          // Filter blank children
          current.children = current.children.filter((child) => child != "");
          // Set current to this new tag
          prev = current;
          current = current.children[
            current.children.length - 1
          ] as _ParserNode;
        }
        // Reset content
        chunks = [];
        // Jump I forward to the close of the tag.
        // If i was at the first character of '<strong>after the tag', it would jump to 'after the tag'
        i = close;
      } else {
        // In this case, there is no tag.
        // Push content onto children.
        if (chunks.length === 0) chunks.push("");
        chunks[chunks.length - 1] += data.charAt(i);
      }
    } else {
      // In this case, there is no tag.
      // Push content onto children.
      if (chunks.length === 0) chunks.push("");
      chunks[chunks.length - 1] += data.charAt(i);
    }
  }
  // Push any remaining content onto children
  current.children.push(...chunks);
  // Filter blank children
  current.children = current.children.filter((child) => child != "");
  // Return the links and the converted data
  return {
    links,
    obj: tree
  };
}

export function convertHTMLRichTextToXML(
  data: string,
  firstIdWeCanAdd: number,
  font: DXT.OutputFontOptions = { font: "Times New Roman", size: "10pt" }
) {
  let parsed = parseHTMLIntoJSObject(data, firstIdWeCanAdd);
  if (parsed?.links == undefined || parsed?.obj == undefined) {
    return { links: [], xml: undefined };
  }
  const xml = parsed.obj
    .map((node) =>
      _walkHTMLTree(_fontToString(font), node, parsed.links, 0, []).join("")
    )
    .join("");
  return {
    links: parsed.links,
    xml
  };
}

export function removeHTMLTags(text: string): string {
  if (text.length) {
    for (const tag of _allowedRichTextTags) {
      text = text.replaceAll(`<${tag}>`, "").replaceAll(`</${tag}>`, "");
    }
  }
  return text;
}
